import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Button, IconButton, List, ListItem, ListItemText, Stack, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useToast } from '../../context/ToastContext';
import { RoleService } from '../../services/role.service';
import { IRole, IRoleMember } from '../../interfaces/role.interface';
import ConfirmDialog from '../common/ConfirmDialog';
import EmployeeAutocomplete, { EmployeeOption } from '../common/EmployeeAutocomplete';
import { helpWhy } from '../../help/why';

interface Props {
    institutionId: string;
    role: IRole;
}

const RoleMembersTab: React.FC<Props> = ({ institutionId, role }) => {
    const toast = useToast();
    const [members, setMembers] = useState<IRoleMember[]>([]);
    const [loading, setLoading] = useState(true);
    const [newMember, setNewMember] = useState<EmployeeOption | null>(null);
    const [memberToRemove, setMemberToRemove] = useState<IRoleMember | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await RoleService.members(institutionId, role.id);
            setMembers(data);
        } catch (err) {
            toast.showApiError(err, 'Failed to load members');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId, role.id]);

    useEffect(() => { void load(); }, [load]);

    const handleRemove = async (userId: string) => {
        try {
            const current = await RoleService.rolesOfUser(institutionId, userId);
            const next = current.filter(r => r.id !== role.id).map(r => r.id);
            await RoleService.setUserRoles(institutionId, userId, next);
            await load();
            toast.showSuccess('Member removed');
        } catch (err) {
            toast.showApiError(err, 'Failed to remove member');
        }
    };

    const handleAdd = async () => {
        if (!newMember) return;
        try {
            const current = await RoleService.rolesOfUser(institutionId, newMember.id);
            const next = Array.from(new Set([...current.map(r => r.id), role.id]));
            await RoleService.setUserRoles(institutionId, newMember.id, next);
            setNewMember(null);
            await load();
            toast.showSuccess('Member added');
        } catch (err) {
            toast.showApiError(err, 'Failed to add member');
        }
    };

    return (
        <Box data-help-section="roles.members.section">
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
                <EmployeeAutocomplete
                    value={newMember}
                    onChange={setNewMember}
                    label="Add member"
                    data-help="roles.members.field.member"
                    sx={{ flex: '1 1 260px', maxWidth: 400 }}
                />
                <Button variant="contained" disabled={!newMember} onClick={handleAdd} data-help="roles.members.add" {...helpWhy(!newMember ? { kind: 'status', message: 'Pick a person to add first.' } : null)}>Add</Button>
            </Stack>
            {!loading && members.length === 0 && (
                <Typography color="text.secondary">No members yet.</Typography>
            )}
            <List>
                {members.map(member => (
                    <ListItem
                        key={member.userId}
                        secondaryAction={
                            <IconButton aria-label="Remove member" edge="end" data-help="roles.members.remove" onClick={() => setMemberToRemove(member)}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        }
                    >
                        <ListItemText primary={member.fullName || member.username} secondary={member.fullName ? member.username : undefined} />
                    </ListItem>
                ))}
            </List>
            <ConfirmDialog
                open={!!memberToRemove}
                title="Remove member"
                message={`Remove ${memberToRemove?.fullName || memberToRemove?.username || 'this person'} from the ${role.name} role? They will lose the access this role gives.`}
                confirmLabel="Remove"
                confirmColor="error"
                paperProps={{ 'data-help-page': 'roles.members.removeDialog.page' }}
                cancelProps={{ 'data-help': 'roles.members.removeDialog.cancel' }}
                confirmProps={{ 'data-help': 'roles.members.removeDialog.confirm' }}
                onCancel={() => setMemberToRemove(null)}
                onConfirm={() => {
                    const target = memberToRemove;
                    setMemberToRemove(null);
                    if (target) void handleRemove(target.userId);
                }}
            />
        </Box>
    );
};

export default RoleMembersTab;
